/**
 * NotesScratchpad — a free-text pad the recruiter types into while talking.
 * Saving posts an internal note to the contact timeline (POST /contacts/:id/notes).
 */
import { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import api from '../../../services/api';

interface Props {
  contactId: string;
  /** Bump the parent so the timeline/cadence refetches after a note lands. */
  onSaved?: () => void;
}

export default function NotesScratchpad({ contactId, onSaved }: Props) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    const body = text.trim();
    if (!body || saving) return;
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/crm/contacts/${contactId}/notes`, { body });
      setText('');
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onSaved?.();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not save the note.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); } }}
        placeholder="Jot notes while you talk… (saves to the timeline as a note)"
        className="min-h-0 flex-1 resize-none rounded-lg border border-gray-300 bg-white p-3 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
      />
      {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-gray-400">⌘/Ctrl + Enter to save</span>
        <button
          type="button"
          onClick={save}
          disabled={saving || !text.trim()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-amber-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <Check className="h-4 w-4" /> : null}
          {saved ? 'Saved' : 'Save note'}
        </button>
      </div>
    </div>
  );
}
